import { checkLostUrl } from '@/api/lost'

/**
 * Estrae l'ID numerico dall'URL in base al tipo:
 *   type='request' → cerca /siterequest/{id}
 *   type='return'  → cerca /returned/{id}
 */
function extractRefId (url, type) {
  if (!url || !type) return null
  const pattern = type === 'request' ? /\/siterequest\/(\d+)/ : /\/returned\/(\d+)/
  const match = url.match(pattern)
  return match ? parseInt(match[1], 10) : null
}

export default {
  upsert: 'full',
  entityName: 'lost',
  pagination: {
    itemsPerPage: 5,
    page: 1,
    sortBy: 'id',
    descending: true
  },
  fields: [
    // ── 1. Data della perdita ─────────────────────────────────────────────────
    {
      value: 'lost_date',
      text: 'Lost Date',
      type: 'date',
      validators: ['required']
    },

    // ── 2. Protocollo ─────────────────────────────────────────────────────────
    {
      value: 'protocol_id',
      text: 'Protocol',
      type: 'autocomplete',
      table: { sortable: true, filter: true },
      title: 'name',
      validators: ['required']
    },

    // ── 3. Sito (filtrato per protocollo) ─────────────────────────────────────
    {
      value: 'site_id',
      reference: 'protocol_has_site',
      text: 'Site',
      type: 'autocomplete',
      id: 'site_id',
      table: { sortable: true, filter: true },
      title: 'site.name',
      subtitle: item => `${item.site.city} - ${item.site.code}`,
      deps: ['protocol_id'],
      readonly: item => !item.protocol_id,
      filter: (item, search) => {
        if (!search) return { protocol_id: item.protocol_id }
        return {
          'site.name': [
            { 'site.name': `%${search}%` },
            { 'site.city': `%${search}%` },
            { 'site.code': `%${search}%` }
          ],
          protocol_id: item.protocol_id
        }
      },
      validators: ['required']
    },

    // ── 4. Tipo (Site Request o Site Return) ──────────────────────────────────
    {
      value: 'type',
      text: 'Site Request/Return',
      type: 'list',
      list: [
        { value: 'request', text: 'Site Request' },
        { value: 'return', text: 'Site Return' }
      ],
      deps: ['site_id'],
      readonly: item => !item.site_id,
      validators: ['required']
    },

    // ── 5. URL alla Site Request o al Return ──────────────────────────────────
    // Il valore visibile: l'utente incolla/digita l'URL.
    // All'uscita dal campo (blur) il controller valida che il record esista
    // e che protocol_id / site_id coincidano. L'ID estratto viene scritto in
    // item.type_id (campo nascosto sotto).
    {
      value: 'url',
      text: 'URL alla Site Req o Ret',
      type: 'text',
      full: true,
      deps: ['type', 'protocol_id', 'site_id'],
      readonly: item => !item.type || !item.protocol_id || !item.site_id,
      hint: () => 'Es: http://localhost:3000/#/siterequest/123',

      // ── Validatori sincroni (eseguiti al submit) ────────────────────────────
      validators: [
        'required',
        (field, value, item) => {
          if (!value) return true
          // Controllo coerenza URL ↔ type
          if (item.type === 'request' && !value.includes('/siterequest/')) {
            return "L'URL deve contenere /siterequest/ per una Site Request"
          }
          if (item.type === 'return' && !value.includes('/returned/')) {
            return "L'URL deve contenere /returned/ per un Site Return"
          }
          // Controllo che la verifica backend sia avvenuta con successo
          if (item._url_error) return item._url_error
          if (!item.type_id) {
            return "Uscire dal campo per verificare l'URL (premere Tab o fare clic altrove)"
          }
          return true
        }
      ],

      // ── Handler blur: verifica backend + coerenza protocol/site ────────────
      blur: async (field, value, item) => {
        // Reset stato precedente
        item._url_error = null
        item.type_id    = null

        if (!value || !item.type || !item.protocol_id || !item.site_id) return

        // Controllo formato / coerenza sincrona
        if (item.type === 'request' && !value.includes('/siterequest/')) {
          item._url_error = "L'URL deve contenere /siterequest/ per una Site Request"
          return
        }
        if (item.type === 'return' && !value.includes('/returned/')) {
          item._url_error = "L'URL deve contenere /returned/ per un Site Return"
          return
        }

        // Estrazione ID numerico
        const id = extractRefId(value, item.type)
        if (!id) {
          item._url_error =
            "Impossibile estrarre l'ID dall'URL: assicurarsi che l'URL contenga " +
            "un numero intero dopo /siterequest/ o /returned/"
          return
        }

        // Chiamata backend: verifica esistenza + protocol_id + site_id
        try {
          const res = await checkLostUrl(item.type, id)
          const ref  = res.data

          if (String(ref.protocol_id) !== String(item.protocol_id)) {
            item._url_error =
              `Il protocollo del riferimento (ID ${ref.protocol_id}) ` +
              `non corrisponde al protocollo selezionato (ID ${item.protocol_id})`
            return
          }
          if (String(ref.site_id) !== String(item.site_id)) {
            item._url_error =
              `Il sito del riferimento (ID ${ref.site_id}) ` +
              `non corrisponde al sito selezionato (ID ${item.site_id})`
            return
          }

          // Tutto OK: imposta l'ID nascosto
          item.type_id = id
        } catch (e) {
          const status = e?.response?.status
          if (status === 404) {
            item._url_error = `Nessun record trovato con ID ${id} di tipo "${item.type}"`
          } else {
            item._url_error = "Errore durante la verifica dell'URL: " +
              (e?.response?.data?.error || e?.message || 'errore sconosciuto')
          }
        }
      }
    },

    // ── type_id: campo nascosto, impostato dal blur handler di "url" ──────────
    {
      value: 'type_id',
      type: 'hidden'
    },

    // ── 6. File allegato ──────────────────────────────────────────────────────
    {
      value: 'filename',
      text: 'File Upload',
      type: 'file',
      validators: ['required']
    }
  ]
}
